import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * Recent Items Store
 * Tracks recently viewed items for Command Palette
 * Per Phase 6 requirements - stores in localStorage for persistence
 */

export type RecentItemType = 'quotation' | 'customer' | 'job_order'

export interface RecentItem {
  id: string
  type: RecentItemType
  title: string // e.g., "QT-2025-00456"
  subtitle: string // e.g., "Acme Corporation"
  href: string // Navigation path
  viewedAt: number // Unix timestamp
}

interface RecentItemsState {
  items: RecentItem[]
  maxItems: number

  // Actions
  addRecentItem: (item: Omit<RecentItem, 'viewedAt'>) => void
  removeRecentItem: (id: string) => void
  clearRecentItems: () => void
  getRecentItems: (type?: RecentItemType, limit?: number) => RecentItem[]
}

export const useRecentItemsStore = create<RecentItemsState>()(
  persist(
    (set, get) => ({
      items: [],
      maxItems: 10, // Keep last 10 items

      addRecentItem: (item) => {
        set((state) => {
          // Remove existing item with same id (to move to front)
          const filteredItems = state.items.filter((i) => i.id !== item.id)

          // Add new item at the beginning
          const newItem: RecentItem = {
            ...item,
            viewedAt: Date.now(),
          }

          // Keep only maxItems
          const newItems = [newItem, ...filteredItems].slice(0, state.maxItems)

          return { items: newItems }
        })
      },

      removeRecentItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }))
      },

      clearRecentItems: () => {
        set({ items: [] })
      },

      getRecentItems: (type, limit = 5) => {
        const { items } = get()
        let filtered = items

        if (type) {
          filtered = items.filter((item) => item.type === type)
        }

        return filtered.slice(0, limit)
      },
    }),
    {
      name: 'freighai-recent-items',
      storage: createJSONStorage(() => localStorage),
    }
  )
)
